import { Badge } from '@/components/ui/badge';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { cn } from '@/lib/utils';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import React from 'react';

type TransactionStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'confirming'
  | 'converting';

interface TransactionStatusBadgeProps {
  /** Status from transaction - handles unknown values gracefully */
  status: TransactionStatus | string | null | undefined;
  className?: string;
  showIcon?: boolean;
}

// Status configuration using semantic token variants (Canon v1.4.5)
// Uses Badge's built-in variant system for automatic dark mode support
type StatusConfigEntry = {
  label: string;
  variant: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  icon?: React.ComponentType<{ className?: string }>;
  indicatorLabel?: string;
};

const statusConfig: Record<TransactionStatus, StatusConfigEntry> = {
  pending: {
    label: 'Pending',
    variant: 'warning',
    icon: sfIcons.clock,
  },
  processing: {
    label: 'Processing',
    variant: 'info',
    indicatorLabel: 'Processing'
  },
  confirming: {
    label: 'Confirming',
    variant: 'info',
    indicatorLabel: 'Confirming transfer'
  },
  converting: {
    label: 'Converting',
    variant: 'info',
    icon: sfIcons.arrowUpDown,
  },
  completed: {
    label: 'Completed',
    variant: 'success',
    icon: sfIcons.checkCircle2,
  },
  failed: {
    label: 'Failed',
    variant: 'danger',
    icon: sfIcons.error,
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'neutral',
    icon: sfIcons.clock,
  },
  expired: {
    label: 'Expired',
    variant: 'warning',
    icon: sfIcons.alertCircle,
  }
};

export const TransactionStatusBadge = ({
  status,
  className,
  showIcon = true
}: TransactionStatusBadgeProps) => {
  // Use getTransactionStatus() to normalize Bridge API statuses to internal statuses
  // This handles 'settled' → 'completed', 'submitted' → 'pending', etc.
  const normalizedStatus = getTransactionStatus({ status });
  const config = statusConfig[normalizedStatus];

  // Build icon element - either loading indicator or static icon
  const iconElement = showIcon ? (
    config.indicatorLabel ? (
      <InlineLoadingIndicator
        size="sm"
        aria-label={config.indicatorLabel}
      />
    ) : config.icon ? (
      <SFIcon icon={config.icon} size="sm" />
    ) : undefined
  ) : undefined;

  return (
    <Badge
      variant={config.variant}
      className={cn('text-xs', className)}
      icon={iconElement}
    >
      {config.label}
    </Badge>
  );
};

// Helper function to determine status from transaction data
export const getTransactionStatus = (transaction: Record<string, unknown> | null | undefined): TransactionStatus => {
  if (!transaction) return 'pending';

  // Map Bridge API statuses to our internal status
  const bridgeStatus = transaction.status?.toLowerCase();

  switch (bridgeStatus) {
    case 'pending':
    case 'submitted':
      return 'pending';
    case 'processing':
    case 'executing':
      return 'processing';
    case 'confirming':
    case 'confirmation_pending':
      return 'confirming';
    case 'converting':
    case 'conversion_pending':
      return 'converting';
    case 'completed':
    case 'success':
    case 'settled':
      return 'completed';
    case 'failed':
    case 'error':
      return 'failed';
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    case 'expired':
    case 'timeout':
      return 'expired';
    default:
      return 'pending';
  }
};